import { BigNumber } from '@0x/utils'
import assetUtils, { Asset, isNativeTokenForNetwork } from 'app-lib/assetUtils'
import loanUtils from 'app-lib/loanUtils'
import numberUtils, { formatPercent, autoFormat, formatPrecision } from 'app-lib/numberUtils'
import appConfig, { chainConfigs } from 'bzx-common/src/config/appConfig'
import RepayLoanRequest from 'bzx-common/src/domain/borrow/RepayLoanRequest'
import IBorrowedFundsState from 'bzx-common/src/domain/IBorrowedFundsState'
import providerUtils from 'bzx-common/src/lib/providerUtils'
import ExpectedResult from 'bzx-common/src/shared-components/ExpectedResult'
import { RootStore } from 'bzx-common/src/stores'
import _ from 'lodash'
import React, { Component, FormEvent } from 'react'
import { Button, Form, Icon, InfoTip, Input, SliderPercent } from 'ui-framework'
import fulcrumProvider from '../../../services/FulcrumProvider'

export interface IRepayLoanFormProps {
  loan: IBorrowedFundsState
  isMobileMedia: boolean
  rootStore: RootStore
  onSubmit: (request: RepayLoanRequest) => void
  onCancel: () => void
}

interface IRepayLoanFormState {
  inputAmount: string
  isLoading: boolean
  repayAmount: BigNumber
  balanceTooLow: boolean
  percentRatio: number
  assetBalance: BigNumber
  activeToken: Asset
  ratio: number
  collateralizedPercent: BigNumber
  liquidationPrice: BigNumber
  needsApproval: boolean
  spender: string
}

export default class RepayLoanForm extends Component<IRepayLoanFormProps, IRepayLoanFormState> {
  public tokenInfo = assetUtils.getInfo(fulcrumProvider.currentChainId, this.props.loan.loanAsset)
  private _onInputUpdate = _.debounce((value) => this.getExpectedData(value), appConfig.delay, {
    leading: true,
  })
  public _borrowDecimals = assetUtils.getDecimals(
    fulcrumProvider.currentChainId,
    this.props.loan.loanAsset
  )
  constructor(props: IRepayLoanFormProps) {
    super(props)

    this.state = {
      inputAmount: '',
      repayAmount: new BigNumber(0),
      balanceTooLow: false,
      percentRatio: 1,
      assetBalance: new BigNumber(0),
      activeToken: assetUtils.getDefaultActiveToken(
        fulcrumProvider.currentChainId,
        this.props.loan.loanAsset,
        this.props.loan.collateralAsset
      ),
      ratio: 0,
      isLoading: true,
      collateralizedPercent: new BigNumber(NaN),
      liquidationPrice: new BigNumber(NaN),
      needsApproval: false,
      spender: '',
    }
  }

  private setNeedsApproval() {
    const { spendingApprovals } = this.props.rootStore
    const { loanAsset } = this.props.loan

    const needsApproval = spendingApprovals.needsApproval(
      fulcrumProvider.currentChainId,
      loanAsset,
      this.state.spender,
      this.state.repayAmount
    )
    this.setState({ needsApproval })
  }

  public async componentDidMount() {
    const { checkApprovals, needsApproval } = this.props.rootStore.spendingApprovals
    const { amountOwed, loanAsset } = this.props.loan
    const balance = await providerUtils.getTokenBalanceOfUser(fulcrumProvider, loanAsset)
    const repayAmount = amountOwed
    const inputAmount = repayAmount.toFixed()
    const balanceTooLow = balance.isEqualTo(0) || repayAmount.gt(balance) ? true : false
    const ratio = balance.gt(0) ? balance.div(amountOwed).toNumber() : 0
    const spender = chainConfigs[fulcrumProvider.currentChainId].contracts.bzx.toLowerCase()
    await checkApprovals([loanAsset], spender)

    this.setState({
      assetBalance: balance,
      balanceTooLow,
      inputAmount,
      isLoading: false,
      ratio,
      repayAmount,
      spender,
      needsApproval: needsApproval(fulcrumProvider.currentChainId, loanAsset, spender, repayAmount),
    })
  }

  public render() {
    const { amountOwed, collateralAsset, loanAsset, loanData } = this.props.loan
    const { collateralizedPercent, liquidationPrice } = this.state
    const { spriteLogo } = assetUtils.getCommon(loanAsset)

    const loanStatus =
      collateralizedPercent.isNaN() ||
      loanData.maintenanceMargin
        .div(10 ** 17)
        .minus(25)
        .lt(collateralizedPercent)
        ? 'safe'
        : 'risky'
    const submitTitle = this.state.repayAmount.toFixed(this._borrowDecimals, 1)
    const warningMessage = this.state.repayAmount.gt(this.props.loan.amountOwed)
      ? 'The amount should be equal or smaller than the loan.'
      : this.state.balanceTooLow
      ? `You don't have enough ${loanAsset} in your wallet.`
      : this.state.repayAmount.lt(0)
      ? `Please enter a positive number`
      : undefined

    const canSubmit = !warningMessage && this.state.repayAmount.gt(0)
    const balanceVSOwedRatio = this.state.assetBalance.div(this.props.loan.amountOwed)
    const warnEntireRepay =
      this.state.percentRatio === 1 && balanceVSOwedRatio.lte(1.005) && balanceVSOwedRatio.gte(1)

    return (
      <Form onSubmit={this.onSubmitClick} onCancel={this.props.onCancel}>
        <section className="form__left_block">
          <div className="form__info_block">
            <div className="form__info_block__logo">
              <Icon name={spriteLogo} size="auto" className="form__info_block__logo_svg" />
            </div>
            <div className="flex-col width-100p">
              <div className="txt-title txt-default form--asset">Repay</div>
              <div className="borrow-flex--column-row">
                <div className="flex-row-center justify-between">
                  <div className="flex-col margin-top-1">
                    <div className="txt-alternate txt-muted">APR</div>
                    <div className="txt-default txt-subtitle">
                      {formatPercent(this.props.loan.interestRate)}%
                    </div>
                  </div>
                  <InfoTip id="repay-loan-apr-tooltip" top={-3}>
                    The % interest paid per year on your position.
                  </InfoTip>
                </div>
                <div className="flex-row-center justify-between">
                  <div className="flex-col margin-top-1">
                    <div className="txt-alternate txt-muted margin-right-1ch"> {loanAsset}</div>
                    <div className="txt-default txt-subtitle">
                      ~{numberUtils.abbreviate(this.state.assetBalance)}
                    </div>
                  </div>
                  <Icon name="wallet-balance" size={20} top={-3} color="var(--color-txt-muted)" />
                </div>
              </div>
            </div>
          </div>
        </section>
        <section className="form__content">
          <div className="flex-row flex-between">
            <div className="flex-column">
              <div className="txt-subtitle margin-bottom-1">Loan amount</div>
              <div className="txt-title padded-v-1">
                {autoFormat(amountOwed)} {loanAsset}
              </div>
            </div>
            <div className="flex-column width-50p">
              <div className="txt-subtitle margin-bottom-1">You will repay</div>
              <div className="flex-between">
                <div className="txt-title padded-v-1">{loanAsset} </div>
                <div className="form__input-container margin-left-1">
                  <Input
                    className="input-rectangle txt-title txt-muted form__input width-100p"
                    name="repay-input"
                    type="text"
                    value={this.state.inputAmount}
                    onChange={this.onRepayAmountChange}
                    onChangeEmit="value"
                    isLoading={this.state.isLoading}
                    withLoader
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="margin-v-2">
            <SliderPercent
              onInsertMaxValue={this.setAmountByPercentage}
              percentSlider={this.state.percentRatio}
              maxSliderValue={100}
            />
          </div>

          <div className="margin-bottom-1">
            <ExpectedResult
              collaterizationPercents={collateralizedPercent}
              liquidationPrice={liquidationPrice}
              loanToken={loanAsset}
              collateralToken={collateralAsset}
              loanStatus={loanStatus}
              chainId={fulcrumProvider.currentChainId}
            />
          </div>

          {warningMessage && !this.state.isLoading ? (
            <p className="form__warning">{warningMessage}</p>
          ) : this.state.needsApproval ? (
            <Button
              variant="green"
              className="block"
              isLoading={this.state.isLoading}
              onClick={this.approveSpending}>
              Approve spending
            </Button>
          ) : (
            <Button
              type="submit"
              variant="green"
              isLoading={this.state.isLoading}
              title={submitTitle}
              disabled={!canSubmit}>
              Repay {numberUtils.autoFormat(this.state.inputAmount)} {loanAsset}
            </Button>
          )}
          {isNativeTokenForNetwork(fulcrumProvider.currentChainId, loanAsset) && (
            <div className="txt-warning margin-top-1 txt-center">
              Make sure you keep enough {loanAsset} gas token{' '}
              <InfoTip id="tip-gas-token" icon="FAQ" top={-1} iconColor="var(--color-orange-aim)">
                You are about to repay with <b>{loanAsset}</b>, which is the gas token necessary to
                pay for your transactions. Make sure you always keep some.{' '}
              </InfoTip>
            </div>
          )}
          {warnEntireRepay && (
            <div className="txt-warning margin-top-1 txt-center">
              You might not have enough {loanAsset}{' '}
              <InfoTip
                id="tip-repay-entire-loan"
                icon="FAQ"
                top={-1}
                iconColor="var(--color-orange-aim)">
                <p>
                  You have{' '}
                  <b title={this.state.assetBalance.toFixed()}>
                    ~ {autoFormat(this.state.assetBalance)} {loanAsset}
                  </b>{' '}
                  in your wallet and this might not be enough to repay the <b>entire loan</b>.
                </p>
                <p>
                  This is because by the time the transaction is mined, your loan will have accrued
                  slightly more interest.
                </p>
                <p>
                  It is very possible that you won't have any issue but to be sure, it is
                  recommended to have more {loanAsset} in your wallet to repay the entire loan.
                </p>
              </InfoTip>
            </div>
          )}
        </section>
      </Form>
    )
  }

  public approveSpending = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    this.setState({ isLoading: true })
    const success = await this.props.rootStore.spendingApprovals.setInfiniteApprovalForToken(
      this.props.loan.loanAsset,
      this.state.spender
    )
    if (success) {
      this.setNeedsApproval()
    }
    this.setState({ isLoading: false })
  }

  public onSubmitClick = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const { loanAsset, collateralAsset, accountAddress, loanId, amountOwed } = this.props.loan
    const { repayAmount } = this.state
    const repayPercent = repayAmount.multipliedBy(100).dividedBy(amountOwed).integerValue(0)

    this.props.onSubmit(
      new RepayLoanRequest(
        loanAsset,
        collateralAsset,
        accountAddress,
        loanId,
        repayAmount,
        repayPercent,
        amountOwed
      )
    )
  }

  public onRepayAmountChange = (inputAmount: string) => {
    const { assetBalance } = this.state
    const { amountOwed } = this.props.loan
    const repayAmount = new BigNumber(inputAmount)
    const balanceTooLow = assetBalance.isEqualTo(0) || repayAmount.gt(assetBalance) ? true : false

    this.setState(
      {
        inputAmount,
        percentRatio: Number(new BigNumber(inputAmount).div(amountOwed)),
        repayAmount,
        balanceTooLow,
      },
      () => {
        this._onInputUpdate(this.state.inputAmount)
      }
    )
  }

  public setAmountByPercentage = async (percentRatio: number) => {
    const repayAmount =
      percentRatio === 1
        ? this.props.loan.amountOwed
        : new BigNumber(formatPrecision(this.props.loan.amountOwed.times(percentRatio)))
    this.onRepayAmountChange(repayAmount.toFixed())
  }

  private getExpectedData = async (inputAmount: string) => {
    const result =
      this.state.percentRatio < 1
        ? await loanUtils.getRepayChangeEstimate(
            fulcrumProvider,
            this.props.loan,
            new BigNumber(inputAmount),
            this.state.activeToken
          )
        : { collateralizedPercent: new BigNumber(NaN), liquidationPrice: new BigNumber(NaN) }

    this.setState({
      collateralizedPercent: result.collateralizedPercent,
      liquidationPrice: result.liquidationPrice,
    })
    this.setNeedsApproval()
  }
}
